import WebcamClient from "./WebcamClient";
import { GazeResult } from "./types";

export type SmoothingType = "none" | "kalman" | "window" | "ema";

export type WebEyeTrackProxyOptions = {
  worker?: Worker;
  workerFactory?: () => Worker;
  autoAttachClickListener?: boolean;

  clickTarget?: Window | HTMLElement;

  // 메뉴바 등 특정 영역 클릭은 calibration에서 제외
  ignoreClickSelector?: string;

  trackerOptions?: {
    maxPoints?: number;
    clickTTL?: number;
    smoothing?: {
      type?: SmoothingType;
      windowSize?: number;
      alpha?: number;
      dt?: number;
      processNoise?: number;
      measurementNoise?: number;
    };
    output?: {
      includeEyePatch?: boolean;
      includeFaceLandmarks?: boolean;
      includeBlendshapes?: boolean;
    };
  };
};

function createDefaultWorker(): Worker {
  throw new Error('Default worker not available — provide workerFactory option');
}

export default class WebEyeTrackProxy {
  private worker: Worker;
  private webcamClient: WebcamClient;
  private clickHandler?: (e: MouseEvent) => void;
  private clickTarget?: Window | HTMLElement;
  private ignoreClickSelector?: string;
  private workerReady: boolean = false;
  private lastRuntimeInfo: unknown = undefined;
  private _onRuntimeInfo: (info: unknown) => void = () => {
    // optional callback
  };
  private _onDiagnostic: (info: unknown) => void = () => {
    // optional callback
  };

  public status: "idle" | "inference" | "calib" = "idle";

  constructor(webcamClient: WebcamClient, options: WebEyeTrackProxyOptions = {}) {
    console.log("[debug] WebEyeTrackProxy Constructor")
    this.webcamClient = webcamClient;
    const workerFactory = options.workerFactory ?? createDefaultWorker;
    this.worker = options.worker ?? workerFactory();
    this.clickTarget = options.clickTarget;
    this.ignoreClickSelector = options.ignoreClickSelector;
    this.worker.onmessage = (mess) => {
      switch (mess.data.type) {
        case "ready":
          this.workerReady = true;
          this.requestRuntimeInfo();
          break;

        case "stepResult":
          this.onGazeResults(mess.data.result as GazeResult);
          break;

        case "statusUpdate":
          this.status = mess.data.status;
          break;

        case "runtimeInfo":
          this.lastRuntimeInfo = mess.data.info;
          this._onRuntimeInfo(mess.data.info);
          break;

        case "diagnostic":
          this._onDiagnostic(mess.data.info);
          break;

        case "initError":
          this.onWorkerError(mess.data.error ?? "worker init failed");
          break;

        case "stepError":
          this.onWorkerError(mess.data.error ?? "worker step failed");
          break;

        default:
          console.warn(`[WebEyeTrackProxy] Unknown message type: ${mess.data.type}`);
          break;
      }
    };

    this.worker.onerror = (error: ErrorEvent) => {
      this.onWorkerError(`${error.message} @ ${error.filename}:${error.lineno}:${error.colno}`);
    };
    this.worker.onmessageerror = (error: MessageEvent) => {
      this.onWorkerError(`[messageerror] ${String(error.data)}`);
    };

    this.webcamClient.startWebcam(async (frame: ImageData, timestamp: number) => {
      if (!this.workerReady) return;
      if (this.status !== "idle") return;
      try {
        this.worker.postMessage({ type: "step", payload: { frame, timestamp } });
      } catch (error) {
        this.onWorkerError(`[postMessage step] ${String(error)}`);
      }
    }).catch((error: unknown) => {
      this.onWebcamError(error);
    });

    this.worker.postMessage({ type: "init", payload: options.trackerOptions ?? {} });

    if (options.autoAttachClickListener !== false) {
      this.attachClickListener();
    }
  }

  private shouldIgnoreClick(e: MouseEvent): boolean {
    if (!this.ignoreClickSelector) return false;
    const t = e.target;
    if (!(t instanceof Element)) return false;
    return !!t.closest(this.ignoreClickSelector);
  }

  attachClickListener(target?: Window | HTMLElement) {
    if (this.clickHandler) return;

    const targetToUse = target ?? this.clickTarget ?? window;
    this.clickTarget = targetToUse;

    this.clickHandler = (e: MouseEvent) => {
      // 메뉴바(또는 지정 영역) 클릭은 calibration click 보내지 않음
      if (this.shouldIgnoreClick(e)) return;

      let normX: number;
      let normY: number;

      // HTMLElement 기준 정규화 (메뉴바/레이아웃 오프셋 최소화)
      if (targetToUse instanceof HTMLElement) {
        const rect = targetToUse.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width;   // 0~1
        const y = (e.clientY - rect.top) / rect.height;  // 0~1
        normX = x - 0.5;
        normY = y - 0.5;
      } else {
        // window 기준 (기존 방식)
        normX = e.clientX / window.innerWidth - 0.5;
        normY = e.clientY / window.innerHeight - 0.5;
      }

      this.sendClick(normX, normY);
    };

    targetToUse.addEventListener("click", this.clickHandler as EventListener);
  }

  detachClickListener() {
    if (!this.clickHandler || !this.clickTarget) return;
    this.clickTarget.removeEventListener("click", this.clickHandler as EventListener);
    this.clickHandler = undefined;
  }

  sendClick(normX: number, normY: number) {
    this.worker.postMessage({ type: "click", payload: { x: normX, y: normY } });
  }

  startCalibration() {
    this.worker.postMessage({ type: "startCalibration" });
  }

  endCalibration() {
    this.worker.postMessage({ type: "endCalibration" });
  }

  setSmoothing(type: SmoothingType, params?: { windowSize?: number; alpha?: number }) {
    this.worker.postMessage({ type: "setSmoothing", payload: { type, ...(params ?? {}) } });
  }

  setWindowSize(windowSize: number) {
    this.worker.postMessage({ type: "setWindowSize", payload: { windowSize } });
  }

  setEMAAlpha(alpha: number) {
    this.worker.postMessage({ type: "setEMAAlpha", payload: { alpha } });
  }

  setKalmanParams(params: { dt?: number; processNoise?: number; measurementNoise?: number }) {
    this.worker.postMessage({ type: "setKalmanParams", payload: params });
  }

  setOutputOptions(params: {
    includeEyePatch?: boolean;
    includeFaceLandmarks?: boolean;
    includeBlendshapes?: boolean;
  }) {
    this.worker.postMessage({ type: "setOutputOptions", payload: params });
  }

  requestRuntimeInfo() {
    this.worker.postMessage({ type: "getRuntimeInfo" });
  }

  dispose() {
    this.detachClickListener();
    this.worker.terminate();
  }

  onGazeResults: (gazeResult: GazeResult) => void = () => {
    console.warn("onGazeResults callback not set");
  };

  onWebcamError: (error: unknown) => void = (error: unknown) => {
    console.error("[WebEyeTrackProxy] Webcam initialization failed:", error);
  };

  onWorkerError: (error: unknown) => void = (error: unknown) => {
    console.error("[WebEyeTrackProxy] Worker failed:", error);
  };

  get onRuntimeInfo(): (info: unknown) => void {
    return this._onRuntimeInfo;
  }

  set onRuntimeInfo(callback: (info: unknown) => void) {
    this._onRuntimeInfo = callback;
    if (this.lastRuntimeInfo !== undefined) {
      this._onRuntimeInfo(this.lastRuntimeInfo);
    }
  }

  get onDiagnostic(): (info: unknown) => void {
    return this._onDiagnostic;
  }

  set onDiagnostic(callback: (info: unknown) => void) {
    this._onDiagnostic = callback;
  }
}
